import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SubscriptionStatus from './components/SubscriptionStatus';
import VisibilityMatrix from './components/VisibilityMatrix';
import PremiumFeatures from './components/PremiumFeatures';
import PaymentMethods from './components/PaymentMethods';
import SubscriptionHistory from './components/SubscriptionHistory';
import ReferralSystem from './components/ReferralSystem';

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for current subscription
  const [currentSubscription] = useState({
    id: 'sub_001',
    visibilityScope: 'Regional Level',
    monthlyPrice: 8000,
    estimatedReach: '75,000',
    expiryDate: '2025-12-15T23:59:59Z',
    nextChargeDate: '2025-12-15T00:00:00Z',
    autoRenewal: true,
    supportPhone: '677123456',
    premiumFeatures: [
      {
        id: 'featured_listing',
        name: 'Featured Listings',
        description: 'Premium placement for your listings',
        price: 3000,
        icon: 'Star'
      },
      {
        id: 'professional_badge',
        name: 'Professional Badge',
        description: 'Verified seller status',
        price: 2000,
        icon: 'Shield'
      }
    ]
  });

  // Mock data for saved payment methods
  const [savedMethods] = useState([
    {
      id: 'pm_001',
      type: 'orange_money',
      phoneNumber: '677123456',
      isDefault: true,
      isVerified: true,
      addedDate: '2024-10-15T10:30:00Z',
      lastUsed: '2024-11-10T14:20:00Z'
    },
    {
      id: 'pm_002',
      type: 'card',
      number: '4532123456789012',
      brand: 'Visa',
      isDefault: false,
      isVerified: true,
      addedDate: '2024-09-20T16:45:00Z',
      lastUsed: '2024-10-25T09:15:00Z'
    },
    {
      id: 'pm_003',
      type: 'wallet',
      balance: 15000,
      isDefault: false,
      isVerified: true,
      addedDate: '2024-08-10T12:00:00Z'
    }
  ]);

  // Mock data for transaction history
  const [transactions] = useState([
    {
      id: 'txn_001',
      type: 'subscription',
      description: 'Regional Visibility Subscription',
      details: 'Monthly subscription for regional geographic visibility',
      amount: 8000,
      status: 'completed',
      date: '2024-11-01T10:30:00Z',
      paymentMethod: 'orange_money',
      paymentMethodName: 'Orange Money ****3456',
      transactionId: 'OM2024110112345',
      invoiceUrl: '/invoices/inv_001.pdf',
      renewalDate: '2024-12-01T10:30:00Z'
    },
    {
      id: 'txn_002',
      type: 'premium',
      description: 'Featured Listings Package',
      details: 'Premium featured listings for 5 car listings',
      amount: 15000,
      status: 'completed',
      date: '2024-10-28T14:20:00Z',
      paymentMethod: 'card',
      paymentMethodName: 'Visa ****9012',
      transactionId: 'CARD2024102887654',
      invoiceUrl: '/invoices/inv_002.pdf'
    },
    {
      id: 'txn_003',
      type: 'subscription',
      description: 'Department Visibility Subscription',
      details: 'Previous monthly subscription for department visibility',
      amount: 2000,
      status: 'completed',
      date: '2024-10-01T10:30:00Z',
      paymentMethod: 'orange_money',
      paymentMethodName: 'Orange Money ****3456',
      transactionId: 'OM2024100112345',
      invoiceUrl: '/invoices/inv_003.pdf'
    },
    {
      id: 'txn_004',
      type: 'premium',
      description: 'Professional Badge',
      details: 'Professional seller verification badge',
      amount: 2000,
      status: 'failed',
      date: '2024-09-25T16:45:00Z',
      paymentMethod: 'mtn_momo',
      paymentMethodName: 'MTN MoMo ****7890',
      failureReason: 'Insufficient balance in mobile money account'
    }
  ]);

  // Mock data for referral system
  const [referralData] = useState({
    referralCode: 'AUTO2024CM',
    totalReferrals: 7,
    activeReferrals: 5,
    rewardsEarned: 3,
    pendingRewards: 1,
    availableRewards: [
      {
        id: 'reward_001',
        title: 'Free Regional Visibility',
        description: '1 month free regional visibility subscription',
        expiryDate: '2025-01-15T23:59:59Z'
      }
    ],
    recentReferrals: [
      {
        id: 'ref_001',
        name: 'Jean Mballa',
        joinDate: '2024-11-10T08:30:00Z',
        status: 'active'
      },
      {
        id: 'ref_002',
        name: 'Marie Fouda',
        joinDate: '2024-11-05T14:20:00Z',
        status: 'active'
      },
      {
        id: 'ref_003',
        name: 'Paul Nkomo',
        joinDate: '2024-10-28T11:15:00Z',
        status: 'pending'
      }
    ]
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'plans', label: 'Visibility Plans', icon: 'Globe' },
    { id: 'premium', label: 'Premium Features', icon: 'Crown' },
    { id: 'payments', label: 'Payment Methods', icon: 'CreditCard' },
    { id: 'history', label: 'History', icon: 'Receipt' },
    { id: 'referrals', label: 'Referrals', icon: 'Users' }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleUpgradeSubscription = (planId) => {
    navigate('/payment-processing', { 
      state: { 
        type: 'subscription_upgrade', 
        planId,
        returnUrl: '/subscription-management'
      }
    });
  };

  const handleUpgradePremium = (featureIds) => {
    navigate('/payment-processing', { 
      state: { 
        type: 'premium_features', 
        featureIds,
        returnUrl: '/subscription-management'
      }
    });
  };

  const handleManageRenewal = () => {
    // Toggle auto-renewal logic would go here
    console.log('Managing auto-renewal settings');
  };

  const handleSelectPlan = (planId) => {
    console.log('Selected plan:', planId);
  };

  const handleToggleFeature = (featureId, enabled) => {
    console.log('Toggle feature:', featureId, enabled);
  };

  const handleAddPaymentMethod = (methodType) => {
    navigate('/payment-processing', { 
      state: { 
        type: 'add_payment_method', 
        methodType,
        returnUrl: '/subscription-management'
      }
    });
  };

  const handleSetDefaultMethod = (methodId) => {
    console.log('Set default payment method:', methodId);
  };

  const handleRemoveMethod = (methodId) => {
    console.log('Remove payment method:', methodId);
  };

  const handleDownloadInvoice = (transactionId) => {
    console.log('Download invoice for transaction:', transactionId);
  };

  const handleViewTransactionDetails = (transactionId) => {
    console.log('View transaction details:', transactionId);
  };

  const handleInviteFriend = (contact, method) => {
    console.log('Invite friend:', contact, 'via', method);
  };

  const handleClaimReward = (rewardId) => {
    console.log('Claim reward:', rewardId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="mx-auto text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Subscription Management - AutoCasse</title>
        <meta name="description" content="Manage your AutoCasse subscription, visibility plans, premium features, and payment methods" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
            <button onClick={() => navigate('/homepage-dashboard')} className="hover:text-foreground transition-smooth">
              Home
            </button>
            <Icon name="ChevronRight" size={16} />
            <span>Subscription Management</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Subscription Management</h1>
          <p className="text-muted-foreground">
            Control your geographic visibility, premium features, and billing preferences
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-border">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <SubscriptionStatus
                currentSubscription={currentSubscription}
                onUpgrade={handleUpgradeSubscription}
                onManageRenewal={handleManageRenewal}
              />
              
              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="TrendingUp" size={32} className="mx-auto text-primary mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Upgrade Plan</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Expand your reach with higher visibility tiers
                  </p>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setActiveTab('plans')}
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={14}
                  >
                    View Plans
                  </Button>
                </div>

                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Crown" size={32} className="mx-auto text-accent mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Premium Features</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enhance your listings with premium options
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('premium')}
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={14}
                  >
                    Explore Features
                  </Button>
                </div>

                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Users" size={32} className="mx-auto text-success mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Refer Friends</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Earn rewards by inviting friends to AutoCasse
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('referrals')}
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={14}
                  >
                    Start Referring
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'plans' && (
            <VisibilityMatrix
              currentPlan="regional"
              onSelectPlan={handleSelectPlan}
              onUpgrade={handleUpgradeSubscription}
            />
          )}

          {activeTab === 'premium' && (
            <PremiumFeatures
              activeFeatures={currentSubscription?.premiumFeatures}
              onToggleFeature={handleToggleFeature}
              onUpgrade={handleUpgradePremium}
            />
          )}

          {activeTab === 'payments' && (
            <PaymentMethods
              savedMethods={savedMethods}
              onAddMethod={handleAddPaymentMethod}
              onSetDefault={handleSetDefaultMethod}
              onRemoveMethod={handleRemoveMethod}
            />
          )}

          {activeTab === 'history' && (
            <SubscriptionHistory
              transactions={transactions}
              onDownloadInvoice={handleDownloadInvoice}
              onViewDetails={handleViewTransactionDetails}
            />
          )}

          {activeTab === 'referrals' && (
            <ReferralSystem
              referralData={referralData}
              onInviteFriend={handleInviteFriend}
              onClaimReward={handleClaimReward}
            />
          )}
        </div>

        {/* Support Section */}
        <div className="mt-12 p-6 bg-muted/25 rounded-lg">
          <div className="flex items-start space-x-4">
            <Icon name="HelpCircle" size={24} className="text-primary mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">Need Help?</h3>
              <p className="text-muted-foreground mb-4">
                Our support team is here to help you with subscription management, billing questions, and technical issues.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => window.open('https://wa.me/237677123456', '_blank')}
                  iconName="MessageCircle"
                  iconPosition="left"
                  iconSize={16}
                >
                  WhatsApp Support
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/user-profile')}
                  iconName="Mail"
                  iconPosition="left"
                  iconSize={16}
                >
                  Email Support
                </Button>
                <Button
                  variant="outline"
                  iconName="Phone"
                  iconPosition="left"
                  iconSize={16}
                >
                  Call +237 677 123 456
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Bottom Padding */}
      <div className="h-20 md:hidden" />
    </div>
  );
};

export default SubscriptionManagement;